<template>
  <v-card class="w-[43rem]" flat>
    <div class="flex justify-between p-4 border-b border-gray-200">
      <h4 class="form-title">
        {{
          editTask
            ? `Edit ${tableMapping.visitTable?.displayName || 'Visit'}`
            : `Add ${tableMapping.visitTable?.displayName || 'Visit'}`
        }}
      </h4>
      <v-icon @click="cancel">mdi-close</v-icon>
    </div>
    <div class="p-4">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-text-field
          v-model="caseId"
          :label="configLang.caseNo.displayName || 'Case No'"
          disabled
          dense
          outlined
          hide-details
          class="mb-4"
        ></v-text-field>
        <!-- If Email Timeline is Enabled, show Reply Source Options -->
        <template v-if="isEmailTimelineEnabled">
          <v-radio-group v-model="replyType" row class="mt-0 mb-2">
            <v-radio label="Use manual message" value="manual"></v-radio>
            <v-radio label="Use Email template" value="template"></v-radio>
          </v-radio-group>

          <v-select
            v-if="replyType === 'template'"
            v-model="selectedTemplateId"
            :items="templates"
            item-text="name"
            item-value="_id"
            label="Select Email Template"
            dense
            outlined
            hide-details
            class="mb-4"
            :rules="[(v) => !!v || 'Email Template is required']"
          ></v-select>
        </template>

        <div v-if="isEmailTimelineEnabled && replyType === 'manual'" class="mb-4">
          <label class="v-label theme--light text-caption mb-1 block">{{
            configLang.taskTitle?.displayName || 'Task Title'
          }}</label>
          <VueEditor
            v-model="taskTitle"
            :placeholder="configLang.taskTitle?.displayName || 'Task Title'"
            class="bg-white"
          ></VueEditor>
        </div>
        <v-text-field
          v-else-if="!isEmailTimelineEnabled"
          v-model="taskTitle"
          :label="configLang.taskTitle?.displayName || 'Task Title'"
          :rules="[(v) => !!v || `${configLang.taskTitle?.displayName} is required`]"
          dense
          outlined
          hide-details
          class="mb-4"
        ></v-text-field>
        <v-text-field
          v-model="taskDescription"
          :label="configLang.taskDescription.displayName || 'Task Description'"
          dense
          outlined
          hide-details
          class="mb-4"
          v-show="
            configLang?.taskDescription?.formVisibility &&
            (!isEmailTimelineEnabled || replyType === 'manual')
          "
        ></v-text-field>
        <v-autocomplete
          v-model="assignedTo"
          :label="configLang.assignedTo.displayName || 'Assigned To'"
          :items="employees"
          :item-text="getFullName"
          :search-input.sync="searchUser"
          item-value="_id"
          @update:search-input="debounceUser"
          hide-no-data
          dense
          outlined
          hide-details
          class="mb-4"
          v-if="!isEmailTimelineEnabled"
        >
        </v-autocomplete>
        <v-select
          :items="statusOptions"
          v-model="taskStatus"
          :label="`${tableMapping.visitTable?.displayName || 'Visit'} Status`"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          class="mb-4"
        ></v-select>
        <v-text-field
          v-model="taskStartDateTime"
          type="datetime-local"
          label="Due Date Time"
          dense
          outlined
          hide-details
          class="mb-4"
        ></v-text-field>
        <v-select
          v-if="editTask"
          v-model="meetingOutcome"
          :label="configLang.outcome?.displayName || 'Outcome'"
          :items="outcomeOptions"
          item-text="text"
          item-value="value"
          dense
          outlined
          hide-details
          class="mb-4"
        ></v-select>
        <!-- If Email Timeline is Enabled, show Reply Source Options -->

        <v-file-input
          v-if="!isEmailTimelineEnabled || replyType === 'manual'"
          v-model="attachmentFiles"
          :label="'Attachments'"
          multiple
          dense
          outlined
          hide-details
          class="mb-4"
          chips
          small-chips
          prepend-icon=""
          prepend-inner-icon="mdi-paperclip"
          show-size
        ></v-file-input>
      </v-form>
    </div>
    <div class="flex items-center justify-end gap-4 border-t border-gray-200 p-4">
      <v-btn text class="normal-case" @click="cancel"> Cancel </v-btn>
      <v-btn
        :color="$vuetify.theme.currentTheme.primary"
        @click="handleClick"
        class="white--text normal-case"
      >
        <v-icon left>mdi-content-save</v-icon>
        Save
      </v-btn>
    </div>
  </v-card>
</template>
<script>
import { debounce, mergeOptionsDetails, isEmailTimelineEnabled } from '@/utils/common'
import { axiosUrl } from '@/utils/workforceApi'
import { VueEditor } from 'vue2-editor'
export default {
  name: 'AddEditCaseTask',
  components: {
    VueEditor
  },
  inject: {
    getOrgConfig: {
      from: 'getOrgConfig',
      default: () => () => null
    }
  },
  data() {
    return {
      userData: this.$storage.getUniversal('user'),
      valid: true,
      taskTitle: '',
      taskDescription: '',
      caseId: null,
      employees: [],
      searchUser: '',
      assignedTo: null,
      taskStatus: 'PENDING',
      meetingOutcome: null,
      taskStartDateTime: null,
      taskEndDateTime: null,
      statusOptions: [
        { text: 'Pending', value: 'PENDING' },
        { text: 'In-Progress', value: 'IN_PROGRESS' },
        { text: 'Completed', value: 'COMPLETED' }
      ],
      attachmentFiles: [],
      replyType: 'manual',
      selectedTemplateId: null,
      templates: []
    }
  },
  props: {
    editCase: Object,
    editTask: Object,
    caseConfig: Object
  },
  watch: {
    editCase: {
      handler(newVal) {
        this.editCase = newVal
        this.taskStartDateTime = this.$dayjs().format('YYYY-MM-DDTHH:mm')
        this.taskEndDateTime = this.$dayjs().endOf('day').format('YYYY-MM-DDTHH:mm')
        this.populate()
      },
      deep: true
    }
  },
  computed: {
    orgConfig() {
      return this.getOrgConfig()
    },
    configLang() {
      return this.$props.caseConfig.fieldMappings
    },
    isEmailTimelineEnabled() {
      return isEmailTimelineEnabled(this.orgConfig, this.caseConfig?._id)
    },
    tableMapping() {
      return this.$props.caseConfig?.tableTabMapping || {}
    },
    outcomeOptions() {
      const statusArr = this.caseConfig?.userDefinedCaseStatus || []
      const items = []
      statusArr.forEach((category, index) => {
        if (category.categoryName) {
          items.push({ header: category.categoryName })
        }
        const options = category.optionsList || category.options || []
        if (Array.isArray(options)) {
          options.forEach((opt) => {
            if (typeof opt === 'string') {
              items.push({ text: opt, value: opt })
            } else if (typeof opt === 'object' && opt !== null) {
              items.push({
                ...opt,
                text: opt.text || opt.code || opt.name || 'Unknown',
                value: opt.value || opt.code || opt._id
              })
            }
          })
        }
        if (index < statusArr.length - 1) {
          items.push({ divider: true })
        }
      })
      return items
    }
  },
  created() {
    if (this.isEmailTimelineEnabled) {
      this.fetchTemplates()
    }
  },
  methods: {
    async addCaseTask() {
      try {
        let title = this.taskTitle
        if (this.isEmailTimelineEnabled && this.replyType === 'template') {
          const selectedTpl = this.templates.find((t) => t._id === this.selectedTemplateId)
          title = selectedTpl ? `Email: ${selectedTpl.name}` : 'Email Template Sent'
        }

        const payload = {
          caseType: this.caseConfig?.name,
          taskTitle: title,
          taskDescription: (this.replyType === 'manual' && this.taskDescription) || undefined,
          case: this.editCase._id,
          assignedTo: this.assignedTo || undefined,
          taskStatus: this.taskStatus || undefined,
          taskStartDateTime: this.taskStartDateTime || undefined,
          taskEndDateTime: this.taskEndDateTime || undefined,
          meetingDetails: {}
        }
        // If email timeline is enabled, post email reply with attachments only (no task creation)
        if (this.isEmailTimelineEnabled) {
          try {
            const formData = new FormData()
            formData.append('caseId', this.editCase._id)
            if (this.replyType === 'template') {
              formData.append('templateId', this.selectedTemplateId)
            } else {
              formData.append(
                'replyBody',
                `${this.taskTitle}${this.taskDescription ? '\n' + this.taskDescription : ''}`
              )
              if (this.attachmentFiles && this.attachmentFiles.length > 0) {
                for (const file of this.attachmentFiles) {
                  formData.append('attachments', file)
                }
              }
            }
            await this.$axios.post('/orgSetup/inbound-email/reply', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            })
            this.$toast.success('Reply sent successfully')
          } catch (replyError) {
            console.error('Failed to send inbound email reply:', replyError)
            this.$toast.error('Failed to send email reply')
          }
          this.$emit('newTaskAdded')
        } else {
          const response = await this.$axios.post('/collection/case/task', payload)
          this.$emit('newTaskAdded', response?.data?.task)
        }

        this.cancel()
      } catch (error) {
        this.$toast.error(error.response?.data?.message)
        console.log(error)
      }
    },
    async updateCaseTask() {
      try {
        const payload = {
          caseType: this.caseConfig?.name,
          taskTitle: this.taskTitle,
          taskDescription: this.taskDescription,
          taskStatus: this.taskStatus,
          assignedTo: this.assignedTo,
          meetingDetails: {
            _id: this.editTask?.meetingDetails?._id,
            meetingOutCome: this.meetingOutcome || undefined,
            meetingStatus: this.taskStatus || undefined
          }
        }
        const response = await this.$axios.put(
          `/collection/case/task/${this.editTask._id}`,
          payload
        )
        this.$emit('updateTask', response.data?.task)
        this.cancel()
      } catch (error) {
        this.$toast.error(error.response?.data?.message)
        console.log(error)
      }
    },
    handleClick() {
      const isEditorEmpty =
        this.isEmailTimelineEnabled &&
        this.replyType === 'manual' &&
        (!this.taskTitle || this.taskTitle.trim() === '' || this.taskTitle === '<p><br></p>')
      const isValid = this.$refs.form.validate()
      if (!isValid || isEditorEmpty) {
        this.$toast.error('Please fill the required fields')
        return
      }
      if (this.editTask) {
        this.updateCaseTask()
      } else {
        this.addCaseTask()
      }
    },
    getFullName(employee) {
      const firstName = employee.firstName || employee.systemUserId?.firstName || ''
      const lastName = employee.lastName || employee.systemUserId?.lastName || ''
      return (
        `${firstName} ${lastName}`.trim() +
        (employee.wfmRole?.role ? ` (${employee.wfmRole.role})` : '')
      )
    },
    debounceUser: debounce(async function (text) {
      if (!text || this.assignedTo) return true
      this.fetchUsers()
    }, 1000),
    async fetchUsers() {
      try {
        const params = {}
        if (this.searchUser) {
          params.search = this.searchUser
        }
        if (this.userData?.branch && !this.userData?.role?.includes('WFM_ADMIN')) {
          params.branch = this.userData?.branch
        }
        const response = await this.$axios.get(axiosUrl.users.getUsers, {
          params
        })
        this.employees = response.data?.users
      } catch (error) {
        console.log(error)
      }
    },
    cancel() {
      this.attachmentFiles = []
      this.replyType = 'manual'
      this.selectedTemplateId = null
      this.$emit('caseTaskAdded')
      if (!this.editTask) {
        this.$refs.form.reset()
      }
    },
    async fetchTemplates() {
      try {
        const response = await this.$axios.get('/letter-templates?limit=*')
        this.templates = (response.data?.data || []).filter((t) => t.type === 'EMAIL')
      } catch (error) {
        console.error('Error fetching templates:', error)
      }
    },
    populate() {
      if (this.editCase) {
        this.caseId = this.editCase?.caseNo
      }
      if (this.editTask) {
        this.taskTitle = this.editTask?.taskTitle
        this.taskDescription = this.editTask?.taskDescription
        this.caseId = this.editTask?.case?.caseNo
        this.assignedTo = this.editTask?.assignedTo?._id
        this.taskStatus = this.editTask?.taskStatus
        this.taskStartDateTime = this.$dayjs(this.editTask?.taskStartDateTime).format(
          'YYYY-MM-DDTHH:mm'
        )
        this.taskEndDateTime = this.editTask?.taskStatus
        this.taskEndDateTime = this.$dayjs(this.editTask?.taskEndDateTime).format(
          'YYYY-MM-DDTHH:mm'
        )
        const outcome =
          this.editTask?.meetingDetails?.meetingOutCome ||
          this.editTask?.meetingDetails?.meetingOutcome
        this.meetingOutcome =
          typeof outcome === 'object' ? outcome?.value || outcome?.text : outcome
      }
      if (this.editTask?.assignedTo) {
        this.searchUser = this.editTask?.assignedTo?.firstName
        this.fetchUsers()
      }
    }
  },
  async mounted() {
    this.populate()
    this.taskStartDateTime = this.$dayjs().format('YYYY-MM-DDTHH:mm')
    this.taskEndDateTime = this.$dayjs().endOf('day').format('YYYY-MM-DDTHH:mm')
  }
}
</script>
<style scoped>
.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}
</style>
